import { z } from "zod/v4";

import {
  AudienceDtoDescriptions,
  AudienceExcludeCurrencyDtoDescriptions,
  AudienceFilterDtoDescriptions,
  CreateAudienceDtoDescriptions,
  ExpressionNodeQueryDescriptions,
} from "../descriptions/audience-dto.describe";
import { assertDescriptions } from "../descriptions/types";

import { ExperienceCategory } from "./experience-categories";
import { UrlMatchList } from "./url-match";

export const GeoLocation = z.object({
  city: z.string(),
  region: z.string(),
  country: z.string(),
});

export type GeoLocationDto = z.infer<typeof GeoLocation>;

export const AudienceTypeList = ["common", "custom", "advanced"] as const;
export type AudienceType = (typeof AudienceTypeList)[number];

export const AudienceFilterTypeList = [
  // utm and url both mean url query params
  "utm",
  "url",
  "urlPath",
  "device",
  "visitor",
  "purchaseLikelihood",
  "jsExpression",
  "trafficSource",
  "country",
  "referrer",
  "cookie",
  "klaviyo",
  "landingPage",
] as const;

export type AudienceFilterType = (typeof AudienceFilterTypeList)[number];

export const ExpressionTypeList = ["common", "custom"] as const;

export type ExpressionType = (typeof ExpressionTypeList)[number];

export const ExpressionNodeOperator = z.enum([
  "and",
  "or",
  "start_paren",
  "end_paren",
]);

export type ExpressionNodeOperatorType = z.infer<typeof ExpressionNodeOperator>;

export const ExpressionNodeQuery = z.object({
  key: z.string().describe(ExpressionNodeQueryDescriptions.key),
  value: z.string().describe(ExpressionNodeQueryDescriptions.value),
  filter: z
    .enum(UrlMatchList)
    .nullish()
    .describe(ExpressionNodeQueryDescriptions.filter),
  type: z
    .enum(AudienceFilterTypeList)
    .default("utm")
    .describe(ExpressionNodeQueryDescriptions.type),
});

export type ExpressionNodeQueryType = z.infer<typeof ExpressionNodeQuery>;

export const ExpressionNode = z
  .object({
    query: ExpressionNodeQuery.optional(),
    operator: ExpressionNodeOperator.optional(),
  })
  .refine(
    (val) => (val.operator && !val.query) || (!val.operator && val.query),
    { message: "must have one of 'operator' or 'query', and not both" }
  );

export type ExpressionNodeType = z.infer<typeof ExpressionNode>;

export const AudienceActionList = [
  "experienceInclude",
  "experienceExclude",
  "experienceUnassigned",
  "assignVariation",
  "randomVariation",
  "leaveUnassigned",
] as const;

export type AudienceAction = (typeof AudienceActionList)[number];

export const AudienceExcludeCurrencyDtoSchema = z.object({
  exclude: z.boolean().describe(AudienceExcludeCurrencyDtoDescriptions.exclude),
  currency: z
    .string()
    .nullable()
    .describe(AudienceExcludeCurrencyDtoDescriptions.currency),
  country: z
    .string()
    .optional()
    .describe(AudienceExcludeCurrencyDtoDescriptions.country),
  region: z
    .string()
    .optional()
    .describe(AudienceExcludeCurrencyDtoDescriptions.region),
  city: z
    .string()
    .optional()
    .describe(AudienceExcludeCurrencyDtoDescriptions.city),
});

export type AudienceExcludeCurrencyDto = z.infer<
  typeof AudienceExcludeCurrencyDtoSchema
>;

export const AudienceFilterDtoSchema = z.object({
  id: z.uuid().describe(AudienceFilterDtoDescriptions.id),
  priority: z.number().int().describe(AudienceFilterDtoDescriptions.priority),
  action: z
    .enum(AudienceActionList)
    .nullable()
    .describe(AudienceFilterDtoDescriptions.action),
  filterType: z
    .enum(AudienceFilterTypeList)
    .describe(AudienceFilterDtoDescriptions.filterType),
  expression: z
    .array(ExpressionNode)
    .optional()
    .nullable()
    .describe(AudienceFilterDtoDescriptions.expression),
  expressionType: z
    .enum(ExpressionTypeList)
    .nullish()
    .describe(AudienceFilterDtoDescriptions.expressionType),
  variationId: z
    .uuid()
    .nullish()
    .describe(AudienceFilterDtoDescriptions.variationId),
  excludeFromAnalytics: z
    .boolean()
    .default(false)
    .describe(AudienceFilterDtoDescriptions.excludeFromAnalytics),
});

export type AudienceFilterDto = z.infer<typeof AudienceFilterDtoSchema>;

export const AudienceDtoSchema = z.object({
  id: z.uuid().describe(AudienceDtoDescriptions.id),
  experienceId: z.uuid().describe(AudienceDtoDescriptions.experienceId),
  enabled: z.boolean().describe(AudienceDtoDescriptions.enabled),
  excludeCurrency: AudienceExcludeCurrencyDtoSchema.describe(
    AudienceDtoDescriptions.excludeCurrency
  ),
  wholesale: z.boolean().describe(AudienceDtoDescriptions.wholesale),
  audienceType: z
    .enum(AudienceTypeList)
    .optional()
    .describe(AudienceDtoDescriptions.audienceType),
  elseAction: z
    .enum(AudienceActionList)
    .nullable()
    .describe(AudienceDtoDescriptions.elseAction),
  elseVariationId: z
    .uuid()
    .nullish()
    .describe(AudienceDtoDescriptions.elseVariationId),
  elseExcludeFromAnalytics: z
    .boolean()
    .default(false)
    .describe(AudienceDtoDescriptions.elseExcludeFromAnalytics),
  filters: AudienceFilterDtoSchema.array()
    .optional()
    .default([])
    .describe(AudienceDtoDescriptions.filters),
  evaluationFrequency: z
    .number()
    .optional()
    .nullish()
    .describe(AudienceDtoDescriptions.evaluationFrequency),
});

export type AudienceDto = z.infer<typeof AudienceDtoSchema>;

export const CreateAudienceDtoSchema = AudienceDtoSchema.extend({
  experienceId: z
    .uuid()
    .optional()
    .describe(CreateAudienceDtoDescriptions.experienceId),
  enabled: AudienceDtoSchema.shape.enabled.describe(
    CreateAudienceDtoDescriptions.enabled
  ),
  audienceType: AudienceDtoSchema.shape.audienceType.describe(
    CreateAudienceDtoDescriptions.audienceType
  ),
  elseAction: AudienceDtoSchema.shape.elseAction.describe(
    CreateAudienceDtoDescriptions.elseAction
  ),
  elseExcludeFromAnalytics:
    AudienceDtoSchema.shape.elseExcludeFromAnalytics.describe(
      CreateAudienceDtoDescriptions.elseExcludeFromAnalytics
    ),
  filters: AudienceDtoSchema.shape.filters.describe(
    CreateAudienceDtoDescriptions.filters
  ),
  evaluationFrequency: AudienceDtoSchema.shape.evaluationFrequency.describe(
    CreateAudienceDtoDescriptions.evaluationFrequency
  ),
});

export type CreateAudienceDto = z.infer<typeof CreateAudienceDtoSchema>;

export type AudienceActionOption = {
  title: string;
  description: string;
};

export const EXPERIENCE_AUDIENCE_ACTION_OPTIONS = {
  assignVariation: {
    title: "Assign to specific test group",
    description: "Visitor will be assigned to a specific test group.",
  },
  randomVariation: {
    title: "Assign randomly",
    description:
      "Visitor will be assigned to a test group randomly based on the %’s defined.",
  },
  experienceExclude: {
    title: "Exclude from test",
    description:
      "Visitor will be excluded from this test for the entire duration.",
  },
  experienceUnassigned: {
    title: "Do not assign",
    description:
      "Visitor will not be included yet in the test but may be subsequently included.",
  },
} satisfies Partial<Record<AudienceAction, AudienceActionOption>>;

export const CAMPAIGN_EXPERIENCE_AUDIENCE_ACTION_OPTIONS = {
  experienceInclude: {
    title: "Include in Campaign",
    description: "Visitor will be included in this campaign.",
  },
  experienceExclude: {
    title: "Exclude from campaign",
    description:
      "Visitor will be excluded from this campaign for the entire duration.",
  },
  experienceUnassigned: {
    title: "Do not assign",
    description:
      "Visitor will not be included yet in the campaign but may be subsequently included.",
  },
} satisfies Partial<Record<AudienceAction, AudienceActionOption>>;

/**
 * `AudienceAction` is a single domain enum, but the option maps above are
 * per-category *presentation* subsets of it: neither map covers every action.
 * `assignVariation`/`randomVariation` are experiment-only, `experienceInclude`
 * is campaign-only, and `leaveUnassigned` is a legacy spelling that predates
 * `experienceUnassigned` and was never given its own copy.
 *
 * So indexing a map directly with a persisted action is unsound — it yields
 * `undefined` whenever the action is unset, legacy, or saved under the other
 * category. Resolve through {@link getAudienceActionOption} instead of
 * subscripting the maps, so the partiality is handled in one place.
 */
const AUDIENCE_ACTION_ALIASES: Readonly<Record<string, AudienceAction>> = {
  leaveUnassigned: "experienceUnassigned",
};

export const audienceActionOptionsFor = (
  category: ExperienceCategory
): Readonly<Record<string, AudienceActionOption | undefined>> =>
  category === "experiment"
    ? EXPERIENCE_AUDIENCE_ACTION_OPTIONS
    : CAMPAIGN_EXPERIENCE_AUDIENCE_ACTION_OPTIONS;

/**
 * Resolves the display copy for an audience action, following legacy aliases.
 * Returns `undefined` when nothing is selected or the action has no copy in
 * this category, which callers render as "no selection" rather than crashing.
 */
export const getAudienceActionOption = (
  category: ExperienceCategory,
  action: string | null | undefined
): AudienceActionOption | undefined => {
  if (!action) {
    return undefined;
  }
  return audienceActionOptionsFor(category)[
    AUDIENCE_ACTION_ALIASES[action] ?? action
  ];
};

/** Display title for an audience action, or `""` when it does not resolve. */
export const getAudienceActionTitle = (
  category: ExperienceCategory,
  action: string | null | undefined
): string => getAudienceActionOption(category, action)?.title ?? "";

// Compile-time checks: description objects must cover all schema keys
assertDescriptions<
  typeof ExpressionNodeQueryDescriptions,
  ExpressionNodeQueryType
>();
assertDescriptions<
  typeof AudienceExcludeCurrencyDtoDescriptions,
  AudienceExcludeCurrencyDto
>();
assertDescriptions<typeof AudienceFilterDtoDescriptions, AudienceFilterDto>();
assertDescriptions<typeof AudienceDtoDescriptions, AudienceDto>();
